/**
 * §18.5 — what this pay makes due, and when.
 *
 * WHERE IT APPEARS AND WHY
 *
 * On the run, and on the preview before the run exists. §18.5 asks for the
 * deposit date "shown on the run, on the deposit tracker, and in the notice".
 * Putting it on the preview as well is the same argument the preview itself
 * makes -- the operator should see the consequence before committing to it,
 * not after.
 *
 * THE DATES ARE THE SERVER'S, AND THIS COMPUTES NONE OF THEM. Since payroll
 * stage 4 (2026-09-22) `src/payroll/deadlines.ts` on the API derives every
 * date, and tests the $2,500 threshold on the whole quarter rather than on this
 * one run. That file says why at length; the short version is that the plan
 * sheet and the working spreadsheet already disagreed about the Q3 Texas wage
 * report, because 31 October 2026 is a Saturday and only one of the two moved
 * it.
 *
 * IT NAMES THE SCHEDULE, AND ASSUMES NONE. The deposit schedule is a setting
 * the IRS assigns for a calendar year (migration 0048), and the notice says
 * which one dated the deposit. Until 2026-09-22 it applied the monthly rule and
 * said so; now, with no schedule recorded for the pay date, the deposit has no
 * date and the notice says where to record one. A date the operator cannot
 * check is worse than one they have to think about, and an assumed schedule is
 * weeks late for a business that is really semiweekly.
 *
 * IT DOES NOT BLOCK ANYTHING. §18.5: "The notice never blocks a run."
 */

import { Link } from 'react-router-dom';
import type { PayDeadlines } from '../api.js';
import { formatCents } from '../lib/money.js';
import { InfoNote } from './InfoNote.js';
import { settingsTab } from '../lib/settings-tabs.js';

export function PayrollDeadlines({ payDate, deadlines }: { payDate: string; deadlines: PayDeadlines }) {
  const liability = <strong>{formatCents(deadlines.quarterLiabilityCents)}</strong>;
  const threshold = formatCents(deadlines.thresholdCents);

  return (
    <section className="settings-section" aria-labelledby="payroll-deadlines">
      <h2 id="payroll-deadlines">What This Pay Makes Due</h2>

      <p className="muted">
        Dates for pay on {payDate}
        {deadlines.schedule !== null && (
          <>
            , on the <strong>{deadlines.schedule}</strong> federal deposit schedule recorded in Settings
          </>
        )}
        . A date falling on a weekend or legal holiday moves to the next business day.
      </p>

      {/*
        No schedule, no deposit date -- and said, in the notice's own words,
        rather than left as a missing row. A quarter that ended under the
        threshold owes no deposit, so it has nothing to be missing.
      */}
      {deadlines.schedule === null && deadlines.requirement !== 'ended_under' && (
        <p className="notice notice-warn">
          No federal deposit schedule is recorded for pay on {payDate}, so the deposit has no date here. The IRS
          sets it — monthly or semiweekly — for each calendar year; record it in{' '}
          <Link to={settingsTab('payroll')}>Settings, under Payroll</Link>.
        </p>
      )}

      {/*
        §18.5 — the $2,500 question, answered with the QUARTER's number rather
        than left as a rule the operator has to apply themselves. It was one
        run's number until 2026-09-22, with a sentence apologising for it; two
        runs of $1,400 are under the threshold separately and over it together.
      */}
      <p className="muted">
        {deadlines.requirement === 'ended_under' ? (
          <>
            {deadlines.quarter}'s Form 941 liability was {liability} — under the {threshold} quarterly
            threshold, so the tax may be paid with the return instead of deposited.
          </>
        ) : deadlines.requirement === 'under_so_far' ? (
          <>
            {deadlines.quarter}'s Form 941 liability so far is {liability} — under the {threshold} quarterly
            threshold, but the quarter has not ended. Only a quarter that ends under it may be paid with the
            return, so the deposit date stands.
          </>
        ) : (
          <>
            {deadlines.quarter}'s Form 941 liability so far is {liability} — at or above the {threshold}{' '}
            quarterly threshold, so the tax is deposited rather than paid with the return.
          </>
        )}{' '}
        {/*
          The exception Publication 15 allows and this application does not
          apply. Folded: it never changes what to do at this business's pay, and
          it is worth finding when somebody asks why a quarter was not offered
          the return.
        */}
        <InfoNote label="The previous quarter's exception">
          Publication 15 also lets a quarter be paid with its return when the previous quarter's liability was
          under {threshold}. This application does not apply that: depositing is never penalised, and a quarter
          paid outside the application is one it cannot see in full.
        </InfoNote>
      </p>

      <table className="lines">
        <thead>
          <tr>
            <th>Due</th>
            <th>What</th>
          </tr>
        </thead>
        <tbody>
          {deadlines.items.map((deadline) => (
            <tr key={deadline.kind}>
              <td className="num deadline-date">
                {deadline.date}
                {/*
                  The statutory date is named whenever it moved. Without it the
                  operator sees "2 November" where every IRS page says the 31st,
                  and has no way to tell a correct shift from a wrong date.
                */}
                {deadline.movedFrom && <span className="muted"> was {deadline.movedFrom}</span>}
              </td>
              <td>
                <strong>{deadline.label}</strong>
                <span className="muted"> · {deadline.because}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/*
        Said plainly, and not as a disclaimer in small print. The figures behind
        these dates are the operator's own, and so is the schedule; both are
        things a CPA confirms once and then rarely again.
      */}
      <p className="muted">
        Due dates only — nothing here is filed or paid by this application. Confirm the deposit schedule with your
        accountant. <Link to="/payroll/deposits">See Every Deposit</Link>
      </p>
    </section>
  );
}
